// Loading service for global loader state management
type LoadingCallback = (isLoading: boolean) => void;

class LoadingService {
  private listeners: Set<LoadingCallback> = new Set();
  private blockingListeners: Set<LoadingCallback> = new Set();
  private loadingCount = 0;
  private blockingCount = 0;

  subscribe(callback: LoadingCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Blocking channel: shows the full-screen overlay. Opt-in only — regular
   * requests drive just the top progress bar. Reserve for flows where the
   * user must not interact mid-flight (destructive/irreversible actions).
   */
  subscribeBlocking(callback: LoadingCallback): () => void {
    this.blockingListeners.add(callback);
    return () => this.blockingListeners.delete(callback);
  }

  private notify() {
    const isLoading = this.loadingCount > 0;
    this.listeners.forEach((callback) => callback(isLoading));
  }

  private notifyBlocking() {
    const isBlocking = this.blockingCount > 0;
    this.blockingListeners.forEach((callback) => callback(isBlocking));
  }

  start() {
    this.loadingCount++;
    this.notify();
  }

  stop() {
    this.loadingCount = Math.max(0, this.loadingCount - 1);
    this.notify();
  }

  startBlocking() {
    this.blockingCount++;
    this.notifyBlocking();
  }

  stopBlocking() {
    this.blockingCount = Math.max(0, this.blockingCount - 1);
    this.notifyBlocking();
  }

  reset() {
    this.loadingCount = 0;
    this.blockingCount = 0;
    this.notify();
    this.notifyBlocking();
  }

  isLoading(): boolean {
    return this.loadingCount > 0;
  }
}

export const loadingService = new LoadingService();
